/**
 * Responsive module navigation.
 *
 * Desktop (≥900px): a persistent left sidebar (`<aside>` containing the
 * `ModuleNav` landmark). The hamburger and drawer are hidden via CSS.
 *
 * Mobile (<900px): the persistent sidebar is hidden; a hamburger button opens a
 * slide-in drawer holding the same `ModuleNav`. The drawer:
 *   - traps focus loosely (focuses itself on open, restores on close),
 *   - closes on Escape, on backdrop click, and on navigation,
 *   - closes automatically when the route changes,
 *   - locks body scroll while open.
 *
 * Both surfaces render the identical, scope-gated `ModuleNav`.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useI18n } from '../i18n';
import { ModuleNav } from './ModuleNav';

export function ModuleSidebar() {
  const { t } = useI18n();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Close the drawer whenever the route changes (e.g. a link was followed).
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Escape to close + body scroll lock + focus management while open.
  useEffect(() => {
    if (!open) return;
    drawerRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      triggerRef.current?.focus();
    };
  }, [open]);

  return (
    <>
      {/* Mobile hamburger — hidden on desktop via CSS. */}
      <button
        ref={triggerRef}
        type="button"
        className="nav-hamburger"
        data-tour="modules-trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="module-drawer"
        onClick={() => setOpen(true)}
      >
        <span className="nav-hamburger-bars" aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
        {t.nav.menu}
      </button>

      {/* Persistent desktop sidebar — hidden on mobile via CSS. */}
      <aside className="module-sidebar" data-tour="modules">
        <ModuleNav variant="sidebar" />
      </aside>

      {/* Mobile drawer — rendered only while open. */}
      {open ? (
        <div
          className="drawer-backdrop"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div
            id="module-drawer"
            ref={drawerRef}
            className="drawer"
            role="dialog"
            aria-modal="true"
            aria-label={t.nav.modules}
            tabIndex={-1}
          >
            <div className="drawer-header">
              <span className="drawer-title">{t.nav.menu}</span>
              <button
                type="button"
                className="icon-button"
                onClick={() => setOpen(false)}
                aria-label={t.common.close}
              >
                ✕
              </button>
            </div>
            <ModuleNav variant="drawer" onNavigate={() => setOpen(false)} />
          </div>
        </div>
      ) : null}
    </>
  );
}
